import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';

export interface JiraStatus {
  connected: boolean;
  siteUrl?: string;
  email?: string;
  status?: string;
  lastCheckedAt?: string | null;
}

export interface JiraProjectRow {
  id: string;
  key: string;
  name: string;
  linkedBoardId: string | null;
  linkedBoardName: string | null;
  lastSyncedAt: string | null;
}

export interface JiraRunCounts {
  [projectKey: string]: { created: number; updated: number; skipped: number; comments: number; attachments: number };
}

export interface JiraRun {
  id: string;
  status: 'running' | 'completed' | 'completed_with_errors' | 'failed';
  projectKeys: string[];
  counts: JiraRunCounts;
  warnings: string[];
  errors: Array<{ issueKey: string; message: string }>;
  startedAt: string;
  finishedAt: string | null;
}

export const jiraKeys = {
  all: ['jira'] as const,
  status: () => [...jiraKeys.all, 'status'] as const,
  projects: () => [...jiraKeys.all, 'projects'] as const,
  runs: () => [...jiraKeys.all, 'runs'] as const,
  run: (id: string) => [...jiraKeys.all, 'run', id] as const,
};

export async function getJiraStatus(): Promise<JiraStatus> {
  const { data } = await api.get<JiraStatus>('/jira/status');
  return data;
}

export function useJiraStatus() {
  return useQuery({ queryKey: jiraKeys.status(), queryFn: getJiraStatus, retry: false });
}

export function useJiraProjects(enabled: boolean) {
  return useQuery({
    queryKey: jiraKeys.projects(),
    queryFn: async () => (await api.get<JiraProjectRow[]>('/jira/projects')).data,
    enabled,
  });
}

export function useConnectJira() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { siteUrl: string; email: string; apiToken: string }) =>
      (await api.post<JiraStatus>('/jira/connect', payload)).data,
    // The settings page shows the failure inline (Jira's own message) —
    // without this the global MutationCache toast would double-display it.
    meta: { suppressGlobalToast: true },
    onSuccess: () => qc.invalidateQueries({ queryKey: jiraKeys.all }),
  });
}

export function useDisconnectJira() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => (await api.delete<JiraStatus>('/jira/connection')).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: jiraKeys.all }),
  });
}

export function useStartJiraImport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { projects: Array<{ key: string; boardId?: string }> }) =>
      (await api.post<{ runId: string }>('/jira/import', payload)).data,
    // Failures (409 run-in-progress, validation) render inline on the page.
    meta: { suppressGlobalToast: true },
    onSuccess: () => qc.invalidateQueries({ queryKey: jiraKeys.runs() }),
  });
}

export function useJiraRuns() {
  return useQuery({
    queryKey: jiraKeys.runs(),
    queryFn: async () => (await api.get<JiraRun[]>('/jira/runs')).data,
  });
}

/** Polls while the run is live so the settings page shows progress without a socket. */
export function useJiraRun(id: string | null) {
  return useQuery({
    queryKey: id ? jiraKeys.run(id) : [...jiraKeys.all, 'run', 'none'],
    queryFn: async () => (await api.get<JiraRun>(`/jira/runs/${id}`)).data,
    enabled: !!id,
    refetchInterval: (query) => {
      const { data, error } = query.state;
      if (data) return data.status === 'running' ? 2000 : false;
      // No data yet. Keying the interval off `data` alone meant a single failed
      // fetch stopped the poll for good, stranding the panel on "Starting
      // import..." while the import ran to completion behind it. Keep retrying
      // on a slower cadence so a transient failure heals itself.
      return error ? 5000 : false;
    },
  });
}
